import { useEffect, useRef, useState } from "react";
import {
  FONT_CATALOG,
  SYSTEM_FONT_OPTIONS,
  THEME_TYPOGRAPHY_META,
  extractFontName,
  type TypoMeta,
} from "../meta";
import { cn } from "@/lib/utils/cn";

interface Props {
  typography: { serif: string; sans: string; mono: string };
  onChange: (key: "serif" | "sans" | "mono", stack: string) => void;
}

export function TypographyEditor({ typography, onChange }: Props) {
  return (
    <div>
      <h4 className="text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-2">
        Tipografía{" "}
        <span className="normal-case tracking-normal text-fg-muted opacity-70 font-normal text-[10px]">
          (elegí de la lista o personalizá)
        </span>
      </h4>
      <div className="flex flex-col gap-3">
        {THEME_TYPOGRAPHY_META.map((meta) => (
          <FontPicker
            key={meta.key}
            meta={meta}
            currentStack={typography[meta.key]}
            onChange={(stack) => onChange(meta.key, stack)}
          />
        ))}
      </div>
    </div>
  );
}

function FontPicker({
  meta,
  currentStack,
  onChange,
}: {
  meta: TypoMeta;
  currentStack: string;
  onChange: (stack: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);

  const catalog = FONT_CATALOG[meta.key];
  const systemOpt = SYSTEM_FONT_OPTIONS[meta.key];
  const currentName = extractFontName(currentStack);
  const inCatalog = catalog.some((f) => f.name === currentName);
  const isSystem = currentStack === systemOpt.stack;
  const isCustom = !inCatalog && !isSystem;

  const triggerLabel = isCustom
    ? customValue || "Personalizado"
    : isSystem
      ? systemOpt.name
      : currentName;

  useEffect(() => {
    if (isCustom && !customValue) setCustomValue(currentStack);
    if (isCustom) setCustomMode(true);
  }, [isCustom, currentStack, customValue]);

  // Cerrar al click fuera
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative">
      <div className="text-xs font-medium text-fg">{meta.label}</div>
      <div className="text-2xs text-fg-subtle font-mono mb-1.5">{meta.hint}</div>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="nx-input flex items-center justify-between gap-2 text-left cursor-pointer"
      >
        <span
          className="flex-1 min-w-0 truncate text-sm"
          style={{ fontFamily: currentStack }}
        >
          {triggerLabel}
        </span>
        <span className="text-2xs text-fg-subtle">▾</span>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 mt-1 bg-bg-card border border-border rounded-md shadow-xl z-50 max-h-[320px] overflow-y-auto"
        >
          {catalog.map((f) => (
            <button
              key={f.name}
              role="option"
              aria-selected={f.name === currentName}
              onClick={() => {
                setCustomMode(false);
                onChange(f.stack);
                setOpen(false);
              }}
              className={cn(
                "block w-full text-left px-3 py-2.5 border-b border-border-subtle hover:bg-bg-hover transition-colors",
                f.name === currentName && "bg-accent-soft",
              )}
            >
              <div
                className="text-base leading-tight mb-0.5"
                style={{ fontFamily: f.stack }}
              >
                {f.name}
              </div>
              <div className="text-2xs text-fg-subtle uppercase tracking-wider">
                {f.vibe}
              </div>
            </button>
          ))}

          <button
            role="option"
            aria-selected={isSystem}
            onClick={() => {
              setCustomMode(false);
              onChange(systemOpt.stack);
              setOpen(false);
            }}
            className={cn(
              "block w-full text-left px-3 py-2.5 border-b border-border-subtle hover:bg-bg-hover transition-colors",
              isSystem && "bg-accent-soft",
            )}
          >
            <div
              className="text-base leading-tight mb-0.5"
              style={{ fontFamily: systemOpt.stack }}
            >
              {systemOpt.name}
            </div>
            <div className="text-2xs text-fg-subtle uppercase tracking-wider">
              No requiere carga web — fonts del sistema
            </div>
          </button>

          <button
            role="option"
            aria-selected={isCustom}
            onClick={() => {
              setCustomMode(true);
              setOpen(false);
            }}
            className={cn(
              "block w-full text-left px-3 py-2.5 hover:bg-bg-hover transition-colors",
              isCustom && "bg-accent-soft",
            )}
          >
            <div className="text-sm leading-tight mb-0.5 font-semibold">
              ✎ Personalizado…
            </div>
            <div className="text-2xs text-fg-subtle uppercase tracking-wider">
              Pegá un stack CSS custom
            </div>
          </button>
        </div>
      )}

      {customMode && (
        <input
          type="text"
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v && v !== currentStack) onChange(v);
          }}
          placeholder="'Mi Fuente', Georgia, serif"
          className="nx-input text-2xs font-mono mt-1.5"
        />
      )}
    </div>
  );
}
